/**
 * Hook for streaming plot refinement via SSE
 *
 * Handles the chat-based plot refinement process where the user
 * sends messages and the AI responds with streaming tokens.
 */

import { useCallback, useRef, useState } from 'react'

import { usePixieWorldAuthContext } from '@/shared/auth/pixieWorldAuthContext'
import {
  createPlotRefinementConnection,
  PlotData,
  SSEConnection,
} from '@/shared/api/sseClient'

import { UIChatMessage } from '../types'

interface UseStreamPlotRefinementOptions {
  sessionUuid: string
  onTokenReceived?: (token: string) => void
  onPlotComplete?: (plot: PlotData) => void
  onError?: (error: Error) => void
}

interface UseStreamPlotRefinementReturn {
  startRefinement: (message: string) => Promise<void>
  cancelRefinement: () => void
  isRefining: boolean
  error: string | null
}

/**
 * Hook to manage plot refinement streaming
 */
export function useStreamPlotRefinement({
  sessionUuid,
  onTokenReceived,
  onPlotComplete,
  onError,
}: UseStreamPlotRefinementOptions): UseStreamPlotRefinementReturn {
  const { getAccessToken, refreshToken } = usePixieWorldAuthContext()
  const [isRefining, setIsRefining] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const connectionRef = useRef<SSEConnection | null>(null)

  const cancelRefinement = useCallback(() => {
    if (connectionRef.current) {
      connectionRef.current.close()
      connectionRef.current = null
    }

    setIsRefining(false)
  }, [])

  const startRefinement = useCallback(
    async (message: string) => {
      if (!sessionUuid) {
        const err = new Error('No session UUID provided')

        setError(err.message)
        onError?.(err)

        return
      }

      // Cancel any existing connection
      cancelRefinement()

      // Get access token (refresh if needed)
      let accessToken = getAccessToken()

      if (!accessToken) {
        try {
          await refreshToken()
          accessToken = getAccessToken()
        } catch {
          const err = new Error('Authentication required')

          setError(err.message)
          onError?.(err)

          return
        }
      }

      if (!accessToken) {
        const err = new Error('Unable to get access token')

        setError(err.message)
        onError?.(err)

        return
      }

      setIsRefining(true)
      setError(null)

      try {
        const connection = await createPlotRefinementConnection(
          sessionUuid,
          message,
          accessToken,
          {
            onToken: (content) => {
              console.log('[SSE] onToken:', content)
              onTokenReceived?.(content)
            },
            onComplete: (plot) => {
              console.log('[SSE] onComplete:', plot)
              setIsRefining(false)
              connectionRef.current = null
              onPlotComplete?.(plot)
            },
            onError: (err) => {
              console.log('[SSE] onError:', err.message)
              setIsRefining(false)
              setError(err.message)
              connectionRef.current = null
              onError?.(err)
            },
            onClose: () => {
              console.log('[SSE] onClose')
              setIsRefining(false)
              connectionRef.current = null
            },
          },
        )

        connectionRef.current = connection
      } catch (err) {
        setIsRefining(false)

        const caughtError = err instanceof Error ? err : new Error(String(err))

        setError(caughtError.message)
        onError?.(caughtError)
      }
    },
    [sessionUuid, getAccessToken, refreshToken, cancelRefinement, onTokenReceived, onPlotComplete, onError],
  )

  return {
    startRefinement,
    cancelRefinement,
    isRefining,
    error,
  }
}

/**
 * Helper to create a user chat message
 */
export function createUserMessage(content: string): UIChatMessage {
  return {
    id: `user-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
    role: 'user',
    content,
    timestamp: Date.now(),
  }
}

/**
 * Helper to create an assistant chat message (for streaming)
 */
export function createAssistantMessage(content: string = '', isStreaming: boolean = false): UIChatMessage {
  return {
    id: `assistant-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
    role: 'assistant',
    content,
    timestamp: Date.now(),
    isStreaming,
  }
}
